/**
 * Versioned Serializer
 *
 * A type-safe serializer/deserializer for versioned data: wraps values in a
 * `Versioned<T>` envelope on write and transparently migrates + unwraps on
 * read. API-transparent — the consumer sees `T`, never `Versioned<T>`.
 *
 * Ported near-verbatim from `ersatzteil-scraper` `src/lib/versioned/serializer.ts`
 * (relative imports only — ADR-0005).
 *
 * @see src-js/lib/storage/README.md (versioned-envelope code pattern)
 */

import type { IJsonSchemaPlugin, Versioned } from './types';

import { isVersionedEnvelope } from './envelope';
import { migrateVersionedData } from './migrate';

/** Type-safe serializer interface for versioned data. */
export interface IVersionedSerializer<T> {
  /** Deserialize a JSON string, migrating if needed. Returns the unwrapped value. */
  deserialize(json: string): T;
  /** Whether a JSON string contains data that needs migration. */
  needsMigration(json: string): boolean;
  /** Serialize a value into a versioned JSON string. */
  serialize(value: T): string;
}

/**
 * Creates a versioned serializer for a given plugin: wraps on serialize,
 * detects raw (envelope-less) values as v1, migrates older versions up on
 * deserialize.
 */
export function createVersionedSerializer<T>(
  plugin: IJsonSchemaPlugin<string, T>
): IVersionedSerializer<T> {
  return {
    serialize(value: T): string {
      const envelope: Versioned<T> = { version: plugin.currentVersion, value };
      return JSON.stringify(envelope);
    },

    deserialize(json: string): T {
      const parsed: unknown = JSON.parse(json);
      let envelope: Versioned<unknown>;

      if (isVersionedEnvelope(parsed)) {
        envelope = parsed;
      } else {
        // Raw value without envelope — treat as v1.
        envelope = { version: 1, value: parsed };
      }

      const result = migrateVersionedData(plugin, envelope);
      return result.data.value;
    },

    needsMigration(json: string): boolean {
      const parsed: unknown = JSON.parse(json);
      if (isVersionedEnvelope(parsed)) {
        return parsed.version < plugin.currentVersion;
      }
      return plugin.currentVersion > 1;
    },
  };
}
